import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices";

const STATUS_TABS = [
  { value: "submitted",    label: "Pending",      color: "#D97706", bg: "#FFFBEB" },
  { value: "under_review", label: "In Review",    color: "#0284C7", bg: "#EFF6FF" },
  { value: "approved",     label: "Approved",     color: "#059669", bg: "#ECFDF5" },
  { value: "rejected",     label: "Rejected",     color: "#DC2626", bg: "#FEF2F2" },
  { value: "info_requested",label:"Info Needed",  color: "#7C3AED", bg: "#F5F3FF" },
  { value: "all",          label: "All",          color: "#64748B", bg: "#F8FAFC" },
];

const ID_LABELS = {
  nin:                    "NIN Slip",
  drivers_license:        "Driver's License",
  voters_card:            "Voter's Card",
  international_passport: "Int'l Passport",
};

// ── Document viewer — handles images and PDFs ─────────────────
function DocImage({ src, label }) {
  const [open, setOpen] = useState(false);
  if (!src) return null;

  const isPdf = src.toLowerCase().endsWith(".pdf");

  return (
    <>
      <div className="cursor-pointer" onClick={() => setOpen(true)}>
        {isPdf ? (
          <div className="w-full h-28 bg-red-50 rounded-xl border border-red-100 flex flex-col items-center justify-center gap-1.5 hover:bg-red-100 transition-colors">
            <i className="ri-file-pdf-2-line text-red-400 text-2xl" />
            <p className="text-red-500 text-[10px] font-semibold">PDF — tap to view</p>
          </div>
        ) : (
          <img src={src} alt={label}
            className="w-full h-28 object-cover rounded-xl border border-slate-100 hover:opacity-90 transition-opacity" />
        )}
        <p className="text-slate-400 text-[10px] text-center mt-1">{label}</p>
      </div>

      {/* Lightbox */}
      {open && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={() => setOpen(false)}>
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
            <p className="text-white text-sm font-semibold">{label}</p>
            <div className="flex items-center gap-2">
              {/* Open in new tab — useful for PDF */}
              <a href={src} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors">
                <i className="ri-external-link-line" />
                Open in new tab
              </a>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                <i className="ri-close-line text-white" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden" onClick={e => e.stopPropagation()}>
            {isPdf ? (
              // Inline PDF viewer using browser's built-in renderer
              <iframe
                src={src}
                title={label}
                className="w-full h-full"
                style={{ border: "none" }}
              />
            ) : (
              <div className="h-full flex items-center justify-center p-4">
                <img src={src} alt={label} className="max-w-full max-h-full object-contain rounded-xl" />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Detail panel ──────────────────────────────────────────────
function KYCDetailPanel({ record, onClose, onAction }) {
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [infoMessage, setInfoMessage]     = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showInfoForm, setShowInfoForm]   = useState(false);

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      let body = {};
      if (action === "reject")       body = { reason: rejectionReason };
      if (action === "request-info") body = { message: infoMessage };

      await apiClient.patch(`/mtrade/admin/kyc/${record.userId}/${action}`, body);
      onAction(action);
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || `${action} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  const p = record.personalInfo || {};
  const d = record.idDocument   || {};

  return (
    <div className="fixed inset-0 z-40 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-lg bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <p className="text-slate-800 font-bold">
              {p.firstName} {p.lastName}
            </p>
            <p className="text-slate-400 text-xs mt-0.5">{record.user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full"
              style={{ background: STATUS_TABS.find(s => s.value === record.status)?.bg || "#F8FAFC",
                       color:      STATUS_TABS.find(s => s.value === record.status)?.color || "#64748B" }}>
              {STATUS_TABS.find(s => s.value === record.status)?.label || record.status}
            </span>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
              <i className="ri-close-line text-slate-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 px-6 py-5 space-y-5 overflow-y-auto">
          {/* Personal info */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Personal Information</p>
            <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5">
              {[
                ["Full Name",   `${p.firstName || ""} ${p.middleName || ""} ${p.lastName || ""}`.trim()],
                ["DOB",         p.dateOfBirth],
                ["Gender",      p.gender],
                ["Phone",       p.phoneNumber],
                ["Address",     p.address],
                ["State / LGA", `${p.state || ""} / ${p.lga || ""}`.replace(/^ \/ $/, "—")],
              ].map(([label, value]) => value && (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className="text-slate-700 font-medium text-right max-w-[60%]">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Identity */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Identity Documents</p>
            <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5">
              {[
                ["ID Type",  ID_LABELS[d.type] || d.type],
                ["ID No.",   d.number],
                ["Expiry",   d.expiryDate || "—"],
                ["BVN",      record.bvn?.number ? `••••••${record.bvn.number.slice(-5)}` : "—"],
                ["BVN Verified", record.bvn?.verified ? "✅ Yes" : "❌ No"],
                ["NIN",      record.nin?.number ? `••••••${record.nin.number.slice(-5)}` : "—"],
              ].map(([label, value]) => value && (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-slate-400">{label}</span>
                  <span className="text-slate-700 font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Document images */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Document Images</p>
            <div className="grid grid-cols-3 gap-3">
              <DocImage src={d.frontImage}   label="ID Front" />
              <DocImage src={d.backImage}    label="ID Back" />
              <DocImage src={d.selfieImage}  label="Selfie" />
            </div>
          </div>

          {/* Rejection reason if rejected */}
          {record.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <p className="text-red-600 text-xs font-bold mb-1">Previous rejection reason:</p>
              <p className="text-red-700 text-sm">{record.rejectionReason}</p>
            </div>
          )}

          {/* Review history */}
          {record.history?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Review History</p>
              <div className="space-y-2">
                {[...record.history].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-slate-700 capitalize">{h.action.replace("_", " ")}</span>
                      {h.adminName && <span className="text-slate-400"> by {h.adminName}</span>}
                      {h.note && <p className="text-slate-400 mt-0.5">{h.note}</p>}
                      <p className="text-slate-300 mt-0.5">
                        {new Date(h.timestamp).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {(record.status === "submitted" || record.status === "under_review") && (
          <div className="px-6 py-4 border-t border-slate-100 space-y-3 flex-shrink-0">
            {/* Start review (if submitted) */}
            {record.status === "submitted" && (
              <button onClick={() => handleAction("start-review")} disabled={!!actionLoading}
                className="w-full py-3 rounded-xl bg-blue-50 text-blue-600 text-sm font-bold border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50">
                {actionLoading === "start-review" ? "Starting…" : "Start Review"}
              </button>
            )}

            <div className="flex gap-2">
              <button onClick={() => handleAction("approve")} disabled={!!actionLoading}
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5">
                {actionLoading === "approve"
                  ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <i className="ri-checkbox-circle-line" />}
                Approve
              </button>
              <button onClick={() => { setShowRejectForm(true); setShowInfoForm(false); }}
                className="flex-1 py-3 rounded-xl bg-red-50 text-red-600 text-sm font-bold border border-red-200 hover:bg-red-100 transition-colors">
                <i className="ri-close-circle-line mr-1" />Reject
              </button>
              <button onClick={() => { setShowInfoForm(true); setShowRejectForm(false); }}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200 transition-colors">
                <i className="ri-question-line mr-1" />Info
              </button>
            </div>

            {showRejectForm && (
              <div className="space-y-2">
                <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection (shown to user)..."
                  rows={3} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-red-400 resize-none" />
                <button onClick={() => handleAction("reject")}
                  disabled={!rejectionReason.trim() || !!actionLoading}
                  className="w-full py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold disabled:opacity-50">
                  {actionLoading === "reject" ? "Rejecting…" : "Confirm Rejection"}
                </button>
              </div>
            )}

            {showInfoForm && (
              <div className="space-y-2">
                <textarea value={infoMessage} onChange={e => setInfoMessage(e.target.value)}
                  placeholder="What additional information do you need from the user?"
                  rows={3} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-blue-400 resize-none" />
                <button onClick={() => handleAction("request-info")}
                  disabled={!infoMessage.trim() || !!actionLoading}
                  className="w-full py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold disabled:opacity-50">
                  {actionLoading === "request-info" ? "Sending…" : "Send Info Request"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function KYCView() {
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [records, setRecords]           = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [selected, setSelected]         = useState(null);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        `/mtrade/admin/kyc?status=${statusFilter}&search=${encodeURIComponent(search)}`
      );
      if (res.data.status) {
        setRecords(res.data.data);
        setStatusCounts(res.data.statusCounts || {});
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQueue(); }, [statusFilter, search]);

  const openDetail = async (record) => {
    try {
      const res = await apiClient.get(`/mtrade/admin/kyc/${record.userId}`);
      if (res.data.status) setSelected(res.data.data);
    } catch { /* silent */ }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">KYC Management</h2>
          <p className="text-white/40 text-xs mt-0.5">Review and process identity verification submissions</p>
        </div>
        <button onClick={fetchQueue}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors">
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
        {STATUS_TABS.map(tab => {
          const count = statusCounts[tab.value] || 0;
          const active = statusFilter === tab.value;
          return (
            <button key={tab.value} onClick={() => setStatusFilter(tab.value)}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all border"
              style={{
                background:   active ? tab.bg    : "transparent",
                color:        active ? tab.color  : "rgba(255,255,255,0.3)",
                borderColor:  active ? tab.color + "40" : "rgba(255,255,255,0.08)",
              }}>
              {tab.label}
              {count > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: active ? tab.color + "20" : "rgba(255,255,255,0.08)", color: active ? tab.color : "rgba(255,255,255,0.3)" }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
      </div>

      {/* Queue */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        {loading ? (
          <div className="divide-y divide-white/4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 bg-white/5 rounded animate-pulse" />
                  <div className="h-2.5 w-24 bg-white/5 rounded animate-pulse" />
                </div>
                <div className="h-6 w-20 bg-white/5 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-3">
              <i className="ri-shield-check-line text-white/20 text-xl" />
            </div>
            <p className="text-white/30 text-sm">No KYC submissions found</p>
          </div>
        ) : (
          <div className="divide-y divide-white/4">
            {records.map(record => {
              const p   = record.personalInfo || {};
              const tab = STATUS_TABS.find(s => s.value === record.status);
              const initials = `${p.firstName?.[0] || ""}${p.lastName?.[0] || ""}` || "?";

              return (
                <div key={record._id}
                  onClick={() => openDetail(record)}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-white/3 cursor-pointer transition-colors">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#00C896] text-xs font-bold">{initials}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {p.firstName} {p.lastName}
                    </p>
                    <p className="text-white/30 text-xs truncate">{record.user?.email}</p>
                    <p className="text-white/20 text-[10px] mt-0.5">
                      {ID_LABELS[record.idDocument?.type] || "—"} ·{" "}
                      Submitted {new Date(record.submittedAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                      {record.submissionCount > 1 && ` · ${record.submissionCount}x`}
                    </p>
                  </div>

                  {/* Status badge */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                      style={{ background: tab?.bg || "#F8FAFC", color: tab?.color || "#64748B" }}>
                      {tab?.label || record.status}
                    </span>
                    <i className="ri-arrow-right-s-line text-white/20" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <KYCDetailPanel
          record={selected}
          onClose={() => setSelected(null)}
          onAction={() => { setSelected(null); fetchQueue(); }}
        />
      )}
    </div>
  );
}
